import { useMutation } from "@apollo/client";
import {
  Accordion,
  Button,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconBrandTiktok,
  IconCheck,
  IconMail,
  IconPackage,
  IconShieldCheck,
  IconUser,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { RESEND_CONFIRMATION_EMAIL } from "../../../apis/lambda/GraphQL/Email/mutations";
import TikTokLoginButton from "../../../components/TikTokLoginButton/TikTokLoginButton";
import { useSignupCompletion } from "../../../hooks/useSignupCompletion";
import classes from "./SignupCompletePage.module.css";

function SignupCompletePage() {
  const theme = useMantineTheme();
  const location = useLocation();
  const email: string | undefined = location.state?.email;

  const {
    isEmailVerified,
    isProfileComplete,
    isBrandApproved,
    isThirdStepComplete,
    allStepsComplete,
    userType,
    user,
  } = useSignupCompletion();

  const isBrand = userType === "BRAND_USER";
  const profileUrlPath = isBrand ? user?.brandId : user?.email;

  const getInitialAccordion = () => {
    if (!isEmailVerified) return "email";
    if (!isProfileComplete) return "profile";
    if (isBrand && !isBrandApproved) return "approval";
    if (!isThirdStepComplete) return "third";
    return null;
  };

  const [activeAccordion, setActiveAccordion] = useState<string | null>(
    getInitialAccordion()
  );

  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendConfirmationEmail, { loading: resendLoading }] = useMutation(
    RESEND_CONFIRMATION_EMAIL,
    {
      onCompleted: () => {
        notifications.show({
          title: "Confirmation email sent!",
          message: "Please check your inbox.",
          color: "green",
        });
        setResendDisabled(true);
        setTimeout(() => setResendDisabled(false), 60000); // Re-enable after 60 seconds
      },
      onError: () => {
        notifications.show({
          title: "Error",
          message: "Failed to send confirmation email. Please try again.",
          color: "red",
        });
      },
    }
  );

  const handleResendEmail = () => {
    const userEmail = email || user?.email;
    if (userEmail) {
      resendConfirmationEmail({ variables: { email: userEmail } });
    }
  };

  useEffect(() => {
    const currentStepCompleted =
      (activeAccordion === "email" && isEmailVerified) ||
      (activeAccordion === "profile" && isProfileComplete) ||
      (activeAccordion === "approval" && isBrandApproved) ||
      (activeAccordion === "third" && isThirdStepComplete);

    if (activeAccordion && currentStepCompleted) {
      setActiveAccordion(getInitialAccordion());
    }
  }, [
    isEmailVerified,
    isProfileComplete,
    isBrandApproved,
    isThirdStepComplete,
    activeAccordion,
  ]);

  const handleAccordionChange = (value: string | null) => {
    if (value) {
      const isCompleted =
        (value === "email" && isEmailVerified) ||
        (value === "profile" && isProfileComplete) ||
        (value === "approval" && isBrandApproved) ||
        (value === "third" && isThirdStepComplete);
      if (isCompleted) {
        return;
      }
    }
    setActiveAccordion(value);
  };

  const checkIcon = <IconCheck style={{ color: theme.colors.green[6] }} />;

  return (
    <Container size="md" py="xl">
      <Paper p="xl" radius="lg" shadow="md" withBorder>
        <Stack align="center" gap="lg">
          <Title order={2}>
            Welcome! Let's get you ready{" "}
            {isBrand ? "to find creators" : "for brands"}.
          </Title>
          <Text c="dimmed" ta="center">
            Complete these steps to go live and start collaborating.
          </Text>

          <Accordion
            variant="separated"
            value={activeAccordion}
            onChange={handleAccordionChange}
            w="100%"
          >
            <Accordion.Item
              value="email"
              className={isEmailVerified ? classes.completed : ""}
            >
              <Accordion.Control
                icon={isEmailVerified ? checkIcon : <IconMail size="1.2rem" />}
              >
                1. Verify your email
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <Text size="sm" c="dimmed" ta="center">
                    We've sent a confirmation link to{" "}
                    <strong>{email || user?.email || "your email"}</strong>.
                    Please check your inbox.
                  </Text>
                  {!isEmailVerified && (
                    <Group justify="center">
                      <Button
                        size="sm"
                        onClick={handleResendEmail}
                        loading={resendLoading}
                        disabled={resendDisabled}
                      >
                        {resendDisabled ? "Email sent" : "Resend email"}
                      </Button>
                    </Group>
                  )}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item
              value="profile"
              className={isProfileComplete ? classes.completed : ""}
            >
              <Accordion.Control
                icon={
                  isProfileComplete ? checkIcon : <IconUser size="1.2rem" />
                }
              >
                2. Complete your profile
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" c="dimmed" ta="center">
                  {isBrand
                    ? "A complete profile helps creators learn about your brand."
                    : "A complete profile helps brands find you."}
                </Text>
                <Group justify="center" mt="md">
                  <Button
                    component={Link}
                    to={`/profile/${profileUrlPath}?edit=true`}
                  >
                    Go to Profile
                  </Button>
                </Group>
              </Accordion.Panel>
            </Accordion.Item>

            {isBrand && (
              <Accordion.Item
                value="approval"
                className={isBrandApproved ? classes.completed : ""}
              >
                <Accordion.Control
                  icon={
                    isBrandApproved ? (
                      checkIcon
                    ) : (
                      <IconShieldCheck size="1.2rem" />
                    )
                  }
                >
                  3. Account approval
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="md">
                    <Text size="sm" c="dimmed" ta="center">
                      Your brand account is pending approval from our team and your brand owner.
                      We'll notify you via email once your account is activated.
                    </Text>
                    <Text size="xs" c="dimmed" ta="center" fw={500}>
                      Need help? Contact your brand's account owner or reach out to us.
                    </Text>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            )}

            {isBrand ? (
              <Accordion.Item
                value="third"
                className={isThirdStepComplete ? classes.completed : ""}
              >
                <Accordion.Control
                  icon={
                    isThirdStepComplete ? (
                      checkIcon
                    ) : (
                      <IconPackage size="1.2rem" />
                    )
                  }
                >
                  4. Add a product
                </Accordion.Control>
                <Accordion.Panel>
                  <Text size="sm" c="dimmed" ta="center">
                    Add products for creators to promote and collaborate on.
                  </Text>
                  <Group justify="center" mt="md">
                    <Button component={Link} to="/products">
                      Go to Products
                    </Button>
                  </Group>
                </Accordion.Panel>
              </Accordion.Item>
            ) : (
              <Accordion.Item
                value="third"
                className={isThirdStepComplete ? classes.completed : ""}
              >
                <Accordion.Control
                  icon={
                    isThirdStepComplete ? (
                      checkIcon
                    ) : (
                      <IconBrandTiktok size="1.2rem" />
                    )
                  }
                >
                  3. (Recommended) Link your TikTok
                </Accordion.Control>
                <Accordion.Panel>
                  <Text size="sm" c="dimmed" ta="center">
                    Connect your TikTok to show brands your engagement and
                    reach.
                  </Text>
                  <Group justify="center" mt="md">
                    <TikTokLoginButton />
                  </Group>
                </Accordion.Panel>
              </Accordion.Item>
            )}
          </Accordion>

          <Button component={Link} to="/discover" variant="subtle" mt="xl">
            {allStepsComplete ? "Go to App" : "Skip for now"}
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}

export default SignupCompletePage;
